import * as React from 'react';
import styled from 'styled-components';

import { Box, Text, Columns, Column } from '@origin-digital/ods-core';
import { ContractAccount } from '@origin-digital/formatters';
import { PaymentInformationCard } from '@origin-digital/payments';

import { FuelInformation } from 'src/components/FuelInformation';

import { IContext } from 'src/context/AppContext';
import {
  isKrakenId,
  isLpg,
  formatReceiptNumber,
  formatDate,
  getPaymentCardType,
} from 'src/utils/helpers';

import * as content from './content.json';

interface IProps {
  children?: JSX.Element | string;
  label: string;
  hideBottomBorder?: boolean;
  size?: 'xs' | 'sm';
  'data-id'?: string;
}

const PaymentInformationCardWrapper = styled(Box)`
  svg {
    margin-top: -2px;
  }
`;

export const Definition: React.SFC<IProps> = ({
  label,
  size = 'xs',
  children,
  'data-id': dataId = 'definition',
}) => {
  const variant = size === 'xs' ? 'body-small' : 'body';

  return (
    <Columns alignY="center">
      <Column width="flex">
        <Text variant={variant} data-id={`${dataId}-label`}>
          {label}
        </Text>
      </Column>
      <Column width="content">
        {children && (
          <Text variant={variant} tone="neutralDark" data-id={`${dataId}-definition`}>
            {children}
          </Text>
        )}
      </Column>
    </Columns>
  );
};

export const AddressDefinition = ({ address }: Pick<IContext, 'address'>) =>
  address ? <Definition label={address} data-id="address" /> : null;

export const DivisionDefinition = ({ divisionId }: Pick<IContext, 'divisionId'>) =>
  divisionId ? (
    <Definition label={content.fuelType} data-id="fuel-information">
      <FuelInformation divisionId={divisionId} />
    </Definition>
  ) : null;

export const ContractAccountDefinition = ({
  divisionId,
  contractAccountId,
  hideBottomBorder = false,
}: Pick<IContext, 'divisionId' | 'contractAccountId'> & Pick<IProps, 'hideBottomBorder'>) => (
  <Definition
    label={isLpg(divisionId) ? content.customerNumber : content.contractAccountId}
    data-id="contract-account"
    hideBottomBorder={hideBottomBorder}
  >
    {isKrakenId(contractAccountId) ? (
      contractAccountId
    ) : (
      <ContractAccount>{contractAccountId}</ContractAccount>
    )}
  </Definition>
);

export const OrderDefinition = ({
  divisionId,
  orderNumber,
}: Pick<IContext, 'divisionId' | 'orderNumber'>) =>
  orderNumber && isLpg(divisionId) ? (
    <Definition label={content.orderNumber} data-id="order-number">
      <ContractAccount>{orderNumber}</ContractAccount>
    </Definition>
  ) : null;

export const ReferenceDefinition = ({ receiptNumber }: Pick<IContext, 'receiptNumber'>) => (
  <Definition label={content.referenceNumber} data-id="receipt">
    {formatReceiptNumber(receiptNumber)}
  </Definition>
);

export const TimestampDefinition = ({ timestamp }: Pick<IContext, 'timestamp'>) => (
  <Definition label={content.date} data-id="timestamp">
    {formatDate(timestamp)}
  </Definition>
);

export const PaymentDefinition = ({
  paymentData,
  paymentMethod,
}: Pick<IContext, 'paymentData' | 'paymentMethod'>) => (
  <Definition label={content.paidWith} data-id="card-details">
    <PaymentInformationCardWrapper>
      {paymentMethod ? (
        <PaymentInformationCard
          cardNumber={paymentMethod.cardNumber}
          paymentCardTypeId={paymentMethod.paymentCardTypeId}
          layout="horizontal"
          color="grey56"
        />
      ) : (
        <PaymentInformationCard
          cardNumber={paymentData.cardNumber}
          paymentCardTypeId={getPaymentCardType(paymentData.cardNumber)}
          layout="horizontal"
          color="grey56"
        />
      )}
    </PaymentInformationCardWrapper>
  </Definition>
);
